import React, { useEffect, useState } from 'react';

const SCORE_TIERS = {
  excellent: {
    gradient: 'from-emerald-400 via-teal-400 to-cyan-400',
    text: 'text-emerald-700',
    label: 'text-emerald-600',
    track: 'bg-emerald-50/60',
    trackBorder: 'ring-1 ring-emerald-200/40',
    dot: 'bg-emerald-400',
    glowColor: 'rgba(16, 185, 129, 0.22)',
    shadowColor: 'rgba(16, 185, 129, 0.30)',
  },
  high: {
    gradient: 'from-primary-400 via-teal-400 to-cyan-400',
    text: 'text-primary-700',
    label: 'text-primary-600',
    track: 'bg-primary-50/60',
    trackBorder: 'ring-1 ring-primary-200/40',
    dot: 'bg-primary-400',
    glowColor: 'rgba(90, 175, 167, 0.22)',
    shadowColor: 'rgba(90, 175, 167, 0.30)',
  },
  mid: {
    gradient: 'from-amber-400 via-orange-400 to-orange-300',
    text: 'text-amber-700',
    label: 'text-amber-600',
    track: 'bg-amber-50/60',
    trackBorder: 'ring-1 ring-amber-200/40',
    dot: 'bg-amber-400',
    glowColor: 'rgba(245, 158, 11, 0.20)',
    shadowColor: 'rgba(245, 158, 11, 0.28)',
  },
  low: {
    gradient: 'from-rose-400 via-red-400 to-orange-400',
    text: 'text-rose-700',
    label: 'text-rose-600',
    track: 'bg-rose-50/60',
    trackBorder: 'ring-1 ring-rose-200/40',
    dot: 'bg-rose-400',
    glowColor: 'rgba(244, 63, 94, 0.20)',
    shadowColor: 'rgba(244, 63, 94, 0.28)',
  },
};

const getTier = (score) => {
  if (score >= 90) return SCORE_TIERS.excellent;
  if (score >= 70) return SCORE_TIERS.high;
  if (score >= 50) return SCORE_TIERS.mid;
  return SCORE_TIERS.low;
};

const SIZE_MAP = {
  sm: {
    bar: 'h-1.5',
    text: 'text-xs',
    radius: 'rounded-full',
    showGlow: false,
    showDot: false,
    showMarks: false,
    showShadow: false,
  },
  md: {
    bar: 'h-2',
    text: 'text-sm',
    radius: 'rounded-full',
    showGlow: true,
    showDot: true,
    dotSize: 6,
    showMarks: false,
    showShadow: true,
  },
  lg: {
    bar: 'h-3',
    text: 'text-sm',
    radius: 'rounded-full',
    showGlow: true,
    showDot: true,
    dotSize: 8,
    showMarks: true,
    showShadow: true,
  },
};

const ScoreBar = ({
  score = 0,
  label,
  showValue = true,
  size = 'md',
  animate = true,
  className = '',
}) => {
  const [width, setWidth] = useState(animate ? 0 : score);
  const [displayValue, setDisplayValue] = useState(animate ? 0 : Math.round(score));
  const tier = getTier(score);
  const sizeStyle = SIZE_MAP[size] || SIZE_MAP.md;

  useEffect(() => {
    if (!animate) {
      setWidth(score);
      setDisplayValue(Math.round(score));
      return;
    }
    let start = null;
    const duration = 800;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const val = score * ease;
      setWidth(val);
      setDisplayValue(Math.round(val));
      if (progress < 1) requestAnimationFrame(step);
    };
    const id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [score, animate]);

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {label && (
        <span className={`${sizeStyle.text} font-medium text-gray-500 flex-shrink-0 min-w-[5rem]`}>
          {label}
        </span>
      )}

      <div className="flex-1 min-w-0 relative">
        {sizeStyle.showGlow && width > 5 && (
          <div
            className={`absolute inset-x-0 top-0.5 ${sizeStyle.bar} ${sizeStyle.radius} blur-[6px] pointer-events-none`}
            style={{
              width: `${Math.min(width, 100)}%`,
              background: `linear-gradient(to right, transparent 10%, ${tier.glowColor})`,
              opacity: 0.6,
            }}
          />
        )}

        <div
          className={`relative ${tier.track} ${tier.trackBorder} ${sizeStyle.radius} ${sizeStyle.bar} overflow-hidden`}
          style={{ boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)' }}
        >
          {sizeStyle.showMarks && [25, 50, 75].map(mark => (
            <div
              key={mark}
              className="absolute top-0 bottom-0 w-px bg-black/[0.05] pointer-events-none z-[1]"
              style={{ left: `${mark}%` }}
            />
          ))}

          <div
            className={`${sizeStyle.bar} ${sizeStyle.radius} bg-gradient-to-r ${tier.gradient} relative overflow-hidden`}
            style={{
              width: `${Math.min(width, 100)}%`,
              boxShadow: sizeStyle.showShadow
                ? `0 1px 4px -1px ${tier.shadowColor}, inset 0 1px 0 rgba(255,255,255,0.18)`
                : undefined,
            }}
          >
            {size !== 'sm' && (
              <div
                className="absolute inset-0 -translate-x-full animate-shimmer"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 35%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 65%, transparent 100%)',
                }}
              />
            )}
          </div>
        </div>

        {sizeStyle.showDot && width > 3 && (
          <div
            className="absolute top-1/2 pointer-events-none z-10"
            style={{
              left: `calc(${Math.min(width, 100)}% - ${sizeStyle.dotSize / 2}px)`,
              transform: 'translateY(-50%)',
            }}
          >
            <span
              className={`block rounded-full ${tier.dot} ring-[1.5px] ring-white`}
              style={{
                width: sizeStyle.dotSize,
                height: sizeStyle.dotSize,
                boxShadow: `0 0 ${sizeStyle.dotSize}px ${tier.glowColor}, 0 1px 2px rgba(0,0,0,0.1)`,
              }}
            />
          </div>
        )}
      </div>

      {showValue && (
        <span
          className={`${sizeStyle.text} font-semibold tabular-nums flex-shrink-0 ${tier.label}`}
        >
          {displayValue}%
        </span>
      )}
    </div>
  );
};

export default ScoreBar;
